import { Form, Select, Row, Col, DatePicker, TimePicker, notification, Upload, Input } from "antd";
import TextArea from "antd/lib/input/TextArea";
import { PrimaryButton } from "../../styled/Button.styled";
import { LightBlueContainer, WhiteContainer } from "../../styled/Container.styled";
import { useForm } from "antd/es/form/Form";
import dayjs from "dayjs";
import { useLazyGetCommunicationByIdQuery, useSaveCommunicationMutation, useSaveCommunicationWithImageMutation } from "../../../feature/Communication/communicationApiSlice";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { PlusOutlined } from "@ant-design/icons";

const { Option } = Select;

const receivers = [
  {
    label: "Users",
    value: {
      type: "Users",
      riders: []
    }
  },
  {
    label: "Drivers",
    value: {
      type: "Drivers",
      drivers: []
    }
  },
  {
    label: "All",
    value: {
      type: "All",
      riders: [],
      drivers: []
    }
  }
];

const CommunicationAddDetails = () => {
  const { id } = useParams();
  const [communicationForm] = useForm();
  const [saveCommunication, { isLoading }] = useSaveCommunicationMutation();
  const [saveCommunicationWithImage, { isLoading: isImageUploading }] = useSaveCommunicationWithImageMutation();
  const [getComById] = useLazyGetCommunicationByIdQuery();
  const navigate = useNavigate();
  const location = useLocation();
  const [disable, setDisable] = useState(false);
  const [fileList, setFileList] = useState<any[]>([]);

  const onClose = () => {
    const urlParams = new URLSearchParams(location.search);
    const page = urlParams.get('page') || '1';
    navigate(`/communication?page=${page}`);
  };

  const MAX_IMAGE_SIZE_MB = 1;

  // Rejects an image larger than 1 MB before it is added; otherwise it is kept and uploaded on Save.
  const beforeImageUpload = (file: any) => {
    if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
      notification.error({
        message: "Image too large",
        description: `Image size must be ${MAX_IMAGE_SIZE_MB} MB or less.`,
        placement: "bottomLeft",
      });
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const handleUploadChange = ({ fileList: newFileList }: any) => {
    setFileList(newFileList);
  };

  const formSubmit = (value: any) => {
  
    const parsed = value.name ? JSON.parse(value.name) : { type: "Users" };
    const receiverType = parsed.type || "Users";
    const allRecipients = receiverType === "All";

    const { publishDate, publishTime, message, title, description } = value;

    // Check if there's an image to upload
    if (fileList.length > 0) {
      const formData = new FormData();
      const communicationDTO: any = {
        receiverType,
        allRecipients,
        publishDate: publishDate ? dayjs(publishDate).format("YYYY-MM-DD") : undefined,
        publishTime: publishTime ? dayjs(publishTime).format("HH:mm") : undefined,
        message,
        title,
        description,
        riders: [],
        drivers: []
      };

      if (receiverType === "Users") {
        communicationDTO.riders = Array.isArray(parsed.riders) ? parsed.riders : [];
      } else if (receiverType === "Drivers") {
        communicationDTO.drivers = Array.isArray(parsed.drivers) ? parsed.drivers : [];
      } else if (receiverType === "All") {
        communicationDTO.riders = Array.isArray(parsed.riders) ? parsed.riders : [];
        communicationDTO.drivers = Array.isArray(parsed.drivers) ? parsed.drivers : [];
      }

      formData.append("communicationDTO", JSON.stringify(communicationDTO));
      formData.append("image", fileList[0].originFileObj);

      console.log("Communication payload with image:", communicationDTO);

      saveCommunicationWithImage(formData)
        .unwrap()
        .then(() => {
          notification.success({
            message: "Successfully Added!",
            placement: "bottomLeft",
          });
          onClose();
        })
        .catch((error: any) => {
          notification.error({
            message: "Error",
            description: error.data?.message || "Failed to save communication",
            placement: "bottomLeft",
          });
        });
    } else {
      const fd: any = {
        receiverType,
        allRecipients,
        publishDate: publishDate ? dayjs(publishDate).format("YYYY-MM-DD") : undefined,
        publishTime: publishTime ? dayjs(publishTime).format("HH:mm") : undefined,
        message,
        title,
        description,
        riders: [],
        drivers: []
      };

      if (receiverType === "Users") {
        fd.riders = Array.isArray(parsed.riders) ? parsed.riders : [];
      } else if (receiverType === "Drivers") {
        fd.drivers = Array.isArray(parsed.drivers) ? parsed.drivers : [];
      } else if (receiverType === "All") {
        fd.riders = Array.isArray(parsed.riders) ? parsed.riders : [];
        fd.drivers = Array.isArray(parsed.drivers) ? parsed.drivers : [];
      }

      
      console.log("Communication payload:", fd);

      saveCommunication(fd)
        .unwrap()
        .then(() => {
          notification.success({
            message: "Successfully Added!",
            placement: "bottomLeft",
          });
          onClose();
        })
        .catch((error: any) => {
          notification.error({
            message: "Error",
            description: error.data?.message || "Failed to save communication",
            placement: "bottomLeft",
          });
        });
    }
  };

  useEffect(() => {
    if (id) {
      setDisable(true);
      getComById(id)
        .unwrap()
        .then((res: any) => {
          const data = res?.data || {};

         
          const receiverType = data.receiverType
            || (Array.isArray(data.drivers) && data.drivers.length ? "Drivers"
              : (Array.isArray(data.riders) && data.riders.length ? "Users" : ""));

          const nameValue = id ? receiverType : JSON.stringify({
            type: receiverType,
            riders: Array.isArray(data.riders) ? data.riders : [],
            drivers: Array.isArray(data.drivers) ? data.drivers : []
          });

          communicationForm.setFieldsValue({
            "name": nameValue,
            "title": data.title,
            "description": data.description,
            "message": data.message,
            "publishDate": data.publishDate ? dayjs(data.publishDate) : undefined,
            "publishTime": data.publishTime ? dayjs(data.publishTime, "HH:mm") : undefined,
          });

          // Set image if exists
          if (data.imageUrl) {
            console.log('Setting image URL:', data.imageUrl);
            const imageFile = {
              uid: '-1',
              name: 'image.png',
              status: 'done' as const,
              url: data.imageUrl,
              thumbUrl: data.imageUrl
            };
            console.log('Setting fileList with:', imageFile);
            setFileList([imageFile]);
          } else {
            console.log('No image URL found in data');
            setFileList([]);
          }
        })
        .catch((error: any) => {
          notification.error({
            message: "Error",
            description: error.data?.message || "Failed to load communication",
            placement: "bottomLeft",
          });
        });
    }
  }, [id, getComById, communicationForm]);

  return (
    <>
      <Row justify="end" gutter={20} className="button-bottom-margin">
        <Col>
          <PrimaryButton onClick={onClose}>Back</PrimaryButton>
        </Col>
        <Col>
          <Form name="submitForm">
            <PrimaryButton disabled={disable} loading={isLoading || isImageUploading} onClick={() => communicationForm.submit()} htmlType="submit">Save</PrimaryButton>
          </Form>
        </Col>
      </Row>

      <LightBlueContainer>
        <WhiteContainer>
          <Form form={communicationForm} onFinish={formSubmit}>
            <Row gutter={[32, 22]}>
              <Col span={12}>
                <label>Receiver</label>
                <Form.Item name="name" rules={[
                  {
                    required: true,
                    message: "Please select the receiver"
                  }
                ]}>
                  <Select disabled={disable}>
                    {
                      receivers.map((receiver: any) => {
                        return (
                          <Option key={receiver.label} value={JSON.stringify(receiver.value)}>{receiver.label}</Option>
                        );
                      })
                    }
                  </Select>
                </Form.Item>

                <label>Publish Date</label>
                <Form.Item name="publishDate"
                  rules={[
                    {
                      required: true,
                      message: "Please select publish date",
                    }
                  ]}>
                  <DatePicker disabled={disable} className="w-100" />
                </Form.Item>

                <label>Publish Time</label>
                <Form.Item name="publishTime"
                  rules={[
                    {
                      required: true,
                      message: 'Please select a time'
                    }
                  ]}>
                  <TimePicker disabled={disable} className="w-100" />
                </Form.Item>

                <label>Image</label>
                <Form.Item name="image">
                  {disable ? (
                    <div>
                      {fileList.length > 0 && fileList[0].url ? (
                        <div style={{ 
                          width: '300px', 
                          height: '300px', 
                          border: '1px solid #d9d9d9',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          backgroundColor: '#f5f5f5'
                        }}>
                          <img 
                            src={fileList[0].url} 
                            alt="Communication" 
                            style={{ 
                              maxWidth: '100%', 
                              maxHeight: '100%', 
                              objectFit: 'contain'
                            }} 
                            onLoad={() => console.log('Image loaded successfully')}
                            onError={(e) => {
                              console.error('Image failed to load:', fileList[0].url);
                              // Show fallback if image fails to load
                              e.currentTarget.style.display = 'none';
                              const originalUrl = (fileList[0] as any).originalUrl || fileList[0].url;
                              e.currentTarget.parentElement!.innerHTML = `
                                <div style="padding: 10px; text-align: center; color: #666;">
                                  <div style="font-size: 12px; margin-bottom: 5px;">Image available but cannot be displayed</div>
                                  <a href="${originalUrl}" target="_blank" style="color: #1890ff; text-decoration: underline; font-size: 11px;">Open image in new tab</a>
                                </div>
                              `;
                            }}
                          />
                        </div>
                      ) : (
                        <div style={{ color: '#999', fontStyle: 'italic', padding: '20px', border: '1px dashed #d9d9d9', borderRadius: '4px' }}>
                          No image uploaded
                        </div>
                      )}
                    </div>
                  ) : (
                    <Upload
                      disabled={disable}
                      listType="picture-card"
                      fileList={fileList}
                      onChange={handleUploadChange}
                      beforeUpload={beforeImageUpload}
                      maxCount={1}
                      showUploadList={{
                        showPreviewIcon: false,
                        showRemoveIcon: !disable
                      }}
                    >
                      {fileList.length === 0 && (
                        <div>
                          <PlusOutlined />
                          <div style={{ marginTop: 8 }}>Upload</div>
                        </div>
                      )}
                    </Upload>
                  )}
                </Form.Item>
              </Col>

              <Col span={12}>
              <label>Title</label>
                <Form.Item name="title">
                  <Input disabled={disable} placeholder="Enter notification title" />
                </Form.Item>

                <label>Description</label>
                <Form.Item name="description">
                  <TextArea disabled={disable} rows={3} placeholder="Enter notification description" />
                </Form.Item>
{/*                 
                <p>Message</p>
                <Form.Item name="message"
                  rules={[
                    {
                      required: true,
                      message: "Message is required"
                    }
                  ]}>
                  <TextArea disabled={disable} rows={14} />
                </Form.Item> */}
              </Col>
            </Row>
          </Form>
        </WhiteContainer>
      </LightBlueContainer>
    </>
  );
};

export default CommunicationAddDetails;
